/**
 * Shared bits of the training administration screens: routes, the version
 * label, and turning a server refusal into words.
 */

import type { TrainingModule } from '../../store/api/trainingApi';

/** In-app paths of the training screens (each is routed in App.tsx). */
export const TRAINING_ROUTES = {
  dashboard: '/training',
  newModule: '/training/modules/new',
  editModule: (moduleId: string) => `/training/modules/${moduleId}`,
  assignModule: (moduleId: string) => `/training/modules/${moduleId}/assign`,
} as const;

/** The short, human form of a content hash. */
export function shortHash(hash: string): string {
  return hash.replace(/^sha256:/, '').slice(0, 8);
}

export function VersionLabel({ version, contentHash }: { version: number; contentHash: string }) {
  return (
    <span className="text-xs text-gray-500" title={contentHash} data-testid="training-version-label">
      Version {version} · {shortHash(contentHash)}
    </span>
  );
}

export function ModuleStatusBadge({ module }: { module: Pick<TrainingModule, 'isActive' | 'status' | 'latestVersion' | 'hasDraft'> }) {
  const label = !module.isActive
    ? 'Archived'
    : module.status === 'draft'
      ? 'Draft — not published'
      : module.status === 'published'
        ? `v${module.latestVersion?.version}${module.hasDraft ? ' + draft' : ''}`
        : 'Unversioned';
  const color = !module.isActive
    ? 'bg-gray-100 text-gray-600'
    : module.status === 'draft'
      ? 'bg-yellow-100 text-yellow-800'
      : 'bg-green-100 text-green-800';
  return <span className={`px-2 py-0.5 text-xs rounded-full ${color}`}>{label}</span>;
}

interface ServerError {
  status?: number | string;
  data?: { error?: string; message?: string; details?: unknown };
}

/** A server refusal as a sentence plus any per-field problems. */
export function describeTrainingError(error: unknown): { message: string; issues: string[] } {
  const e = error as ServerError | undefined;
  const data = e?.data;
  const issues = Array.isArray(data?.details)
    ? (data!.details as Array<{ path?: string; message?: string }>).map((d) => `${d.path ?? ''}: ${d.message ?? ''}`.trim())
    : [];
  if (data?.message) return { message: data.message, issues };
  if (e?.status === 403) return { message: 'You do not have permission to manage training in this workspace.', issues };
  if (e?.status === 404) return { message: 'Not found.', issues };
  return { message: 'The request could not be completed. Please try again.', issues };
}

export function TrainingErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  const { message, issues } = describeTrainingError(error);
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert" data-testid="training-error">
      <p>{message}</p>
      {issues.length > 0 && (
        <ul className="mt-1 list-disc pl-5">
          {issues.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
